import { CURRENCY_SPACE, GROUP_SEPARATOR, formatEuros, type MonthSource } from '@cockpit/shared';

/**
 * Mise en forme des écrans de données (lot B2), à l'identique de la maquette (`eur`, `sgn`, `MOIS`) :
 * montants arrondis à l'euro, signe « − » typographique, mois abrégés.
 */

export const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

export const MONTHS_LONG = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

const MINUS = '−';

/** « 84 327 € » (centimes → euros arrondis). */
export function eur(cents: number): string {
  return formatEuros(cents).replace(/^-/, MINUS);
}

/** « 89,90 € » (au centime). */
export function eurCents(cents: number): string {
  return formatEuros(cents, { cents: true }).replace(/^-/, MINUS);
}

/** « +4 085 € », « −1 200 € », « 0 € » (maquette `sgn`). */
export function signedEur(cents: number): string {
  const euros = Math.round(Math.abs(cents) / 100);
  if (euros === 0) return eur(0);
  return `${cents > 0 ? '+' : MINUS}${eur(Math.abs(cents))}`;
}

/** Nombre entier à la française, sans symbole (cases de la heatmap). */
export function int(value: number): string {
  return String(Math.round(Math.abs(value)))
    .replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR)
    .replace(/^/, value < 0 && Math.round(Math.abs(value)) !== 0 ? MINUS : '');
}

/** Euros entiers sans symbole, à partir de centimes (« 3 000 »). */
export function intEuros(cents: number): string {
  return int(cents / 100);
}

/** « 47 % » à partir d'un ratio (0,47). */
export function percent(ratio: number): string {
  return `${Math.round(ratio * 100)}${CURRENCY_SPACE}%`;
}

/** « +47 % », « −12 % ». */
export function signedPercent(ratio: number): string {
  const value = Math.round(ratio * 100);
  if (value === 0) return `0${CURRENCY_SPACE}%`;
  return `${value > 0 ? '+' : MINUS}${Math.abs(value)}${CURRENCY_SPACE}%`;
}

/** Index du mois (0 = janvier) d'une clé `AAAA-MM`. */
export function monthIndex(month: string): number {
  return Number(month.slice(5, 7)) - 1;
}

/** « sept. » pour `2026-09`. */
export function monthShort(month: string): string {
  return MONTHS_SHORT[monthIndex(month)] ?? month;
}

/** « septembre » pour `2026-09`. */
export function monthLong(month: string): string {
  return MONTHS_LONG[monthIndex(month)] ?? month;
}

/** « Septembre » pour `2026-09`. */
export function monthTitle(month: string): string {
  const long = monthLong(month);
  return long.charAt(0).toUpperCase() + long.slice(1);
}

/** « 12 oct. » pour `2026-10-12`. */
export function dayShort(date: string): string {
  return `${Number(date.slice(8, 10))} ${MONTHS_SHORT[Number(date.slice(5, 7)) - 1] ?? ''}`;
}

/** « k » d'un axe (maquette) : `12 k`, `−3 k`, `0`. */
export function axisK(cents: number): string {
  const euros = cents / 100;
  if (euros === 0) return '0';
  const k = Math.abs(euros) / 1000;
  const text = Number.isInteger(k) ? String(k) : k.toFixed(1).replace('.', ',');
  return `${euros < 0 ? MINUS : ''}${text} k`;
}

/** « 43,4 k€ » ou « 900 € » (barres horizontales de la maquette). */
export function compactEur(cents: number): string {
  const euros = Math.round(cents / 100);
  if (Math.abs(euros) >= 1000) return `${(euros / 1000).toFixed(1).replace('.', ',')} k€`;
  return `${euros} €`;
}

/** Puce de source d'une carte à partir des sources des mois (« Axonaut », « Sheet importé »…). */
export function sourcePill(sources: readonly MonthSource[]): string {
  const axonaut = sources.includes('axonaut');
  const imported = sources.includes('import');
  if (axonaut && imported) return 'Axonaut + Sheet';
  if (axonaut) return 'Axonaut';
  if (imported) return 'Sheet importé';
  return 'Aucune donnée';
}

/** Années proposées dans les sélecteurs : 2023 (début de l'historique du Sheet) à l'année en cours. */
export function yearsUntil(currentYear: number, first = 2023): number[] {
  const years: number[] = [];
  for (let y = first; y <= Math.max(first, currentYear); y++) years.push(y);
  return years;
}
